const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Respaldo de la cookie httpOnly: algunos navegadores (Safari, etc.) bloquean cookies entre
// dominios distintos (frontend en Cloudflare, backend en Render). Con este token la sesion
// funciona igual ahi. setAuthToken/clearAuthToken se llaman desde AppContext en login/logout.
export const setAuthToken = (token) => { if (token) localStorage.setItem("yad2yad_token", token); };
export const clearAuthToken = () => localStorage.removeItem("yad2yad_token");

const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem("yad2yad_token");
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const fetchOpts = { headers, credentials: "include", ...options };

  // El servidor gratuito a veces "duerme" y tarda unos segundos en despertar con el primer
  // pedido — eso puede cortar la conexion (Safari: "Load failed"). Reintenta una vez antes
  // de mostrar error.
  let res;
  try {
    res = await fetch(`${BASE_URL}${endpoint}`, fetchOpts);
  } catch (e) {
    await new Promise(r => setTimeout(r, 3000));
    try {
      res = await fetch(`${BASE_URL}${endpoint}`, fetchOpts);
    } catch (e2) {
      throw new Error("No se pudo conectar con el servidor. Si el sitio estuvo un rato sin uso, puede tardar unos segundos en arrancar — probá de nuevo.");
    }
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error del servidor");
  return data;
};

export const authAPI = {
  login:       (email, password) =>
    api("/api/auth/login", { method:"POST", body: JSON.stringify({ email, password }) }),
  register:    (name, email, password, inviteCode, community, rabbi, dni) =>
    api("/api/auth/register", { method:"POST", body: JSON.stringify({ name, email, password, inviteCode, community, rabbi, dni }) }),
  verifyEmail: (token) =>
    api("/api/auth/verify-email", { method:"POST", body: JSON.stringify({ token }) }),
  approveUser: (userId) =>
    api("/api/auth/approve-user", { method:"POST", body: JSON.stringify({ userId }) }),
  rejectUser:  (userId) =>
    api("/api/auth/reject-user",  { method:"POST", body: JSON.stringify({ userId }) }),
  setPassword: (token, password) =>
    api("/api/auth/set-password", { method:"POST", body: JSON.stringify({ token, password }) }),
  logout: () =>
    api("/api/auth/logout", { method:"POST" }),
  forgotPassword: (email) =>
    api("/api/auth/forgot-password", { method:"POST", body: JSON.stringify({ email }) }),
  resetPassword: (token, password) =>
    api("/api/auth/reset-password", { method:"POST", body: JSON.stringify({ token, password }) }),
  adminGenerateResetLink: (userId) =>
    api("/api/auth/admin-generate-reset-link", { method:"POST", body: JSON.stringify({ userId }) }),
};

export const salesAPI = {
  createPreference: (data) => api("/api/sales/create-preference", { method:"POST", body: JSON.stringify(data) }),
  confirmCode:      (data) => api("/api/sales/confirm",           { method:"POST", body: JSON.stringify(data) }),
  getMine:          ()     => api("/api/sales/mine"),
  getAll:           ()     => api("/api/sales/all"),
  refund:           (saleId) => api("/api/sales/refund", { method:"POST", body: JSON.stringify({ saleId }) }),
  markPaid:         (saleId) => api("/api/sales/mark-paid", { method:"POST", body: JSON.stringify({ saleId }) }),
};

export const adsAPI = {
  getActive:   ()     => api("/api/ads/active"),
  getAll:      ()     => api("/api/ads/all"),
  create:      (data) => api("/api/ads/create",     { method:"POST", body: JSON.stringify(data) }),
  approve:     (adId) => api("/api/ads/approve",    { method:"POST", body: JSON.stringify({ adId }) }),
  reject:      (adId) => api("/api/ads/reject",     { method:"POST", body: JSON.stringify({ adId }) }),
  deactivate:  (adId) => api("/api/ads/deactivate", { method:"POST", body: JSON.stringify({ adId }) }),
  getMine:     (advertiserId) => api(`/api/ads/mine/${advertiserId}`),
  sendMessage: (adId, message) => api("/api/ads/message", { method:"POST", body: JSON.stringify({ adId, message }) }),
  createPreference: (adId) => api("/api/ads/create-preference", { method:"POST", body: JSON.stringify({ adId }) }),
};

export const uploadAPI = {
  uploadDniPhoto: async (file, userId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    const token = localStorage.getItem("yad2yad_token");
    const res = await fetch(`${BASE_URL}/api/upload/dni-photo`, {
      method: "POST", body: formData, credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error al subir la foto");
    return data;
  },
  getDniPhotoUrl: (adminId, userId) =>
    api("/api/upload/dni-photo-url", { method:"POST", body: JSON.stringify({ adminId, userId }) }),
};

export const notificationsAPI = {
  getMine:     () => api("/api/notifications/mine"),
  markRead:    (notificationId) => api("/api/notifications/read",     { method:"POST", body: JSON.stringify({ notificationId }) }),
  markAllRead: ()                => api("/api/notifications/read-all", { method:"POST" }),
};

export const questionsAPI = {
  getForProduct: (productId) => api(`/api/questions/${productId}`),
  create: (data)   => api("/api/questions/create", { method:"POST", body: JSON.stringify(data) }),
  answer: (data)   => api("/api/questions/answer", { method:"POST", body: JSON.stringify(data) }),
};

export const usersAPI = {
  getAll:    () => api("/api/users/all"),
  updateMe:  (changes) => api("/api/users/me", { method:"PATCH", body: JSON.stringify(changes) }),
};

export const chatsAPI = {
  getMine:       () => api("/api/chats/mine"),
  getOrCreate:   (productId, buyerId) => api("/api/chats/get-or-create", { method:"POST", body: JSON.stringify({ productId, buyerId }) }),
  sendMessage:   (chatId, text) => api("/api/chats/message", { method:"POST", body: JSON.stringify({ chatId, text }) }),
  markRead:      (chatId) => api("/api/chats/read", { method:"POST", body: JSON.stringify({ chatId }) }),
};

export const favoritesAPI = {
  getMine: () => api("/api/favorites/mine"),
  toggle:  (productId) => api("/api/favorites/toggle", { method:"POST", body: JSON.stringify({ productId }) }),
};

export const reviewsAPI = {
  create: (data) => api("/api/reviews/create", { method:"POST", body: JSON.stringify(data) }),
};

export const productsAPI = {
  create: (data)     => api("/api/products/create", { method:"POST",  body: JSON.stringify(data) }),
  update: (id, data) => api(`/api/products/${id}`,   { method:"PATCH", body: JSON.stringify(data) }),
  remove: (id)        => api(`/api/products/${id}`,   { method:"DELETE" }),
  report: (id, reason) => api(`/api/products/${id}/report`, { method:"POST", body: JSON.stringify({ reason }) }),
};

export const inviteCodesAPI = {
  getAll: ()               => api("/api/invite-codes/all"),
  create: (code, description) => api("/api/invite-codes/create", { method:"POST", body: JSON.stringify({ code, description }) }),
  toggle: (id)              => api("/api/invite-codes/toggle", { method:"POST", body: JSON.stringify({ id }) }),
};
